import {keywordFilter} from '../../utils/VisibilityFilter';
import {RootState} from '../../store';
import {DomainName} from "./types.ts";

const key = 'domainNames';

// FNP-942: records moved to React Query. What remains in Redux for this module
// is SELECTION (the checkbox set) and VISIBILITY (page, limit, keyword, columns).
//
// ⚠️ These selectors deliberately no longer read `state.domainNames.records`.
// The old `visibilitySelector` derived `totalCount`/`items` from the records
// slice; leaving that in place while the slice is unpopulated computes paging
// from an empty set, which is a live defect in flexi-email-app today (FNP-950).
// Paging is now derived from the items React Query supplies, passed in.

export const selectionSelector = (state: RootState) => {
    return state[key].selection;
};

export const visibilityStateSelector = (state: RootState) => {
    return state[key].visibility;
};

/**
 * Derives the filtered view from records React Query already fetched.
 *
 * Takes `items` as an argument rather than reading them from the store — the
 * store no longer holds records. Callers pass what `useDomainNamesQuery`
 * returned.
 *
 * ⚠️ FNP-1282: THE OFFSET SLICE IS GONE, and removing it fixed a live defect
 * rather than only tidying the model. `visibilityFilter` ended in `pageFilter`,
 * which cut the list down to `limit` rows — and `limit` defaults to **10** in
 * `VisibilitySliceFor` while the service fetches **50** per request. So the
 * table showed 10 of the 50 rows it had loaded, and "Load more" fetched rows
 * that could never appear. The keyword filter is kept; the slice is not.
 *
 * It also makes the sort correct. Slicing BEFORE sorting orders one arbitrary
 * client-side page instead of the loaded set, which looks like sorting and is
 * not — the same class of failure as `sortable` headers with no `sortStatus`.
 *
 * ⚠️ `keywordFilter` throws on an empty keyword rather than passing everything
 * through, so the empty case is branched here. That is why this does not simply
 * call it unconditionally.
 */
export const selectVisibility = (
    visibility: {
        keyword: string,
        visible: boolean,
        columns: string[]
    },
    items: DomainName[],
    registrarId?: string,
) => {
    const scoped = typeof registrarId !== 'undefined' && registrarId !== ''
        ? items.filter((item: DomainName) => item.registrarId === registrarId)
        : items;

    const {
        keyword,
        visible,
        columns
    } = visibility;

    const visibleItems = keyword
        ? keywordFilter(scoped, keyword) as DomainName[]
        : scoped;

    // Counted against the real fetched set, and it is a LOADED count — under
    // token pagination the browser cannot know a total it has not fetched.
    const visibleCount = keyword ? visibleItems.length : scoped.length;

    return {
        items: scoped,
        visibleItems,
        keyword,
        visible,
        columns,
        visibleCount,
    };
};
